import React, { useState, useEffect, useCallback } from 'react';
import { Languages, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

const LANG_NAME = {
  en: 'English', it: 'Italian', fr: 'French', es: 'Spanish', pt: 'Portuguese',
  ru: 'Russian', uk: 'Ukrainian', ko: 'Korean', zh: 'Mandarin', 'zh-TW': 'Mandarin TW',
};
const FLAG = {
  en: '🇬🇧', it: '🇮🇹', fr: '🇫🇷', es: '🇪🇸', pt: '🇵🇹',
  ru: '🇷🇺', uk: '🇺🇦', ko: '🇰🇷', zh: '🇨🇳', 'zh-TW': '🇹🇼',
};

/**
 * TranslationStatusWidget
 * -----------------------
 * Compact live coverage card embedded at the top of the SuperAdmin page.
 *
 * - Polls /api/admin/arp-coverage every 30 s so SuperAdmin can watch the
 *   bulk translation pipeline fill cells in real time.
 * - Shows per-language progress as a horizontal stacked bar: green = human
 *   verified, amber = AI-translated awaiting review, grey = empty.
 * - Collapsed to a one-line summary by default; click to expand the breakdown.
 * - Hides itself entirely once every language hits 100 %.
 */
export default function TranslationStatusWidget() {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/arp-coverage`);
      if (r.ok) {
        const j = await r.json();
        setData(j);
        setLastUpdated(new Date());
      }
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data || !data.summary) return null;
  const summary = data.summary;
  const langs = Object.keys(summary).filter(l => l !== 'en');
  // Overall progress = total non-EN cells filled / total non-EN expected
  const totalCells = langs.reduce((s, l) => s + (summary[l]?.total || 0), 0);
  const filledCells = langs.reduce((s, l) => s + (summary[l]?.filled || 0), 0);
  const overallPct = totalCells ? Math.round(100 * filledCells / totalCells) : 0;
  const isComplete = overallPct >= 100;
  if (isComplete) return null;

  return (
    <div
      className="bg-gradient-to-r from-indigo-50 via-white to-amber-50 border border-indigo-200 rounded-md p-4 mb-4"
      data-testid="translation-status-widget"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Languages className="w-4 h-4 text-indigo-700 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-zinc-950">
              Translation pipeline · {filledCells} / {totalCells} cells
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-bold tracking-wider align-middle">
                {overallPct}%
              </span>
            </p>
            <p className="text-[11px] text-zinc-600">
              {lastUpdated ? `Live · refreshed ${lastUpdated.toLocaleTimeString()}` : 'Loading…'}
              {refreshing && <span className="ml-2 text-indigo-700">syncing…</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            className="p-1.5 rounded hover:bg-indigo-100 text-indigo-700"
            title="Refresh now"
            data-testid="translation-widget-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="p-1.5 rounded hover:bg-indigo-100 text-indigo-700"
            title={collapsed ? 'Expand' : 'Collapse'}
            data-testid="translation-widget-toggle"
          >
            {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="mt-3 space-y-1.5">
          {langs.map(lang => {
            const row = summary[lang] || { total: 0, filled: 0, amber: 0, percent: 0 };
            const verified = (row.filled || 0) - (row.amber || 0);
            const amber = row.amber || 0;
            const empty = (row.total || 0) - (row.filled || 0);
            const total = Math.max(1, row.total || 0);
            const verifiedPct = (100 * verified) / total;
            const amberPct = (100 * amber) / total;
            const emptyPct = (100 * empty) / total;
            return (
              <div key={lang} className="flex items-center gap-2 text-[11px]" data-testid={`translation-row-${lang}`}>
                <span className="w-24 flex-shrink-0 text-zinc-700">
                  <span className="mr-1">{FLAG[lang] || '🌐'}</span>{LANG_NAME[lang] || lang}
                </span>
                <div className="flex-1 h-3.5 bg-zinc-100 rounded overflow-hidden flex">
                  <div className="bg-emerald-500" style={{ width: `${verifiedPct}%` }} title={`${verified} verified`} />
                  <div className="bg-amber-400" style={{ width: `${amberPct}%` }} title={`${amber} AI-translated`} />
                  <div className="bg-zinc-200" style={{ width: `${emptyPct}%` }} title={`${empty} empty`} />
                </div>
                <span className="w-16 text-right text-zinc-600 font-mono tabular-nums">
                  {row.filled || 0}/{row.total || 0}
                </span>
              </div>
            );
          })}
          <div className="flex items-center gap-3 mt-2 pt-2 border-t border-indigo-100 text-[10px] text-zinc-600">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" />Human-verified</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded-sm" />AI-translated (badge shown to readers)</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-zinc-200 rounded-sm" />Empty</span>
          </div>
        </div>
      )}
    </div>
  );
}
